// The listing. Folders are grouped above files under their own label rather than sorted in among
// them, so a folder is never something you have to scan for. Both groups read the same way in
// either view: a list of rows with size and date, or a grid of cards. A folder's size is all it
// holds, below it too; in Trash, where what a folder holds isn't listed, it has none to give.
//
// A folder opens into the folders and files in it. A file opens into the preview once it is secured — before that
// there is no copy of it the API can read back — and its row carries a menu to open or download
// it. Each row's name is the one control that opens it, stretched over the whole row, so the menu
// beside it is a control of its own rather than one nested inside another.
//
// Every row's menu ends in Rename, Move to… and Delete: the first gives the item a new name, the
// next moves it into another folder, the last sends it to Trash. Trash lists what is in it the same way, except nothing there opens: its rows
// offer only to restore an item or delete it for good.
//
// In the drive itself, rows and cards can also be dragged onto a folder to move them there, in
// either view. Dragging one that is selected takes the whole selection along. A folder lights up
// while what is dragged over it can go in, which is anything but itself. The drag shows the row or
// card it was picked up by, or for several items, how many there are.
//
// Outside Trash, a file's menu also stars it, or takes the star away again. Recent and Starred
// list files alone, under one label of their own rather than Folders and Files, in the order
// they are handed over.
//
// Every row and card also carries a checkbox, for acting on several items at once from the
// selection bar. Like the menu it sits above the stretched name, so ticking it never
// opens the item. A card is laid out the same way as a row for that reason: its name is the
// control, stretched over the card, rather than the card itself.
//
// A file shows its thumbnail where there is one: the whole face of a card, a 36px square in a row. One without — not an image, secured before thumbnails were made, or whose url
// has run out — keeps its kind glyph in a tile of the same size, so the gallery never steps and the
// list never jogs. A file still being secured shimmers there, if it is a kind whose thumbnail is on
// its way. Each kind wears its picture like what it opens into: a video's frame on the player's dark
// ground with a play mark, a PDF's first page on the reader's mat. A card shows the whole picture,
// however it is shaped, between bars of its tile's ground; a row is too small for that, and is
// filled, a page from its top, where titles are. On a card, a video's tile also gives its running
// time and a PDF's its page count; the list has columns for facts, so a row's tile only has to be
// recognised. Text and code have no picture: their opening lines are set as real text on a sheet of
// the reader's paper, prose as it reads with Markdown's headings in bold, code in monospace with its
// indentation kept. At a row's size the lines are too small to read, and give the shape of the page
// instead. A recording's tile is the player's own waveform on its dark ground, with its running time
// on a card; one whose waveform hasn't been measured keeps its glyph rather than wearing a made-up
// one. Over a picture an always-on checkbox is noise, so on a card it waits for the pointer, the
// keyboard, or a selection already under way. Folders have nothing to picture and stay compact.
//
// A file card is its picture, edge to edge, with what is said about it floating on it as glass. A
// chip in the corner gives the kind, with the running time or page count where there is one; the
// name, size and date rise on a caption when the pointer or the keyboard reaches the card or it is
// selected, and stay up on touch screens, which have no hover to wait for. The glass blurs and
// deepens what is behind it rather than covering it, is lit along its top edge and where the pointer
// is, and takes its tint from the tile beneath — dark over photos, video frames and waveforms, pale
// over pages, sheets and glyphs — so its text keeps its contrast whatever the picture. Where the
// browser cannot blur behind an element, or the system has been asked for less transparency, the
// same shapes are drawn in a solid tint.
//
// File cards are laid out in rows, as a photo gallery is: a landscape card is as tall as the row and
// as wide as its picture's shape makes it, and each row fills the width exactly. A portrait card
// would be too narrow for its name at that height, so it keeps a width a name fits in and stands
// taller than the row instead, its picture still portrait. The shape comes with the listing, so a
// card is the right size before its picture arrives and nothing moves as it does. It is held
// between 3:4 and twice as wide as tall, past which the picture is shown whole between bars rather
// than let one card take a row or tower over it; a card with no picture is 4:3. Read left to right,
// the rows keep the order files are listed in.
//
// A listing that comes a page at a time asks for its next page as the file ten from the end of
// those listed so far scrolls into view, in either view, so the next page is usually there before
// the end is reached.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {Link} from 'react-router-dom'
import {useState} from 'react'

import AudioIcon from '@mui/icons-material/AudiotrackOutlined'
import CheckIcon from '@mui/icons-material/CheckRounded'
import Checkbox from '@mui/material/Checkbox'
import CodeIcon from '@mui/icons-material/CodeRounded'
import DocIcon from '@mui/icons-material/DescriptionOutlined'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import EmptyIcon from '@mui/icons-material/FolderOpenOutlined'
import Fade from '@mui/material/Fade'
import FileIcon from '@mui/icons-material/InsertDriveFileOutlined'
import FolderIcon from '@mui/icons-material/FolderRounded'
import IconButton from '@mui/material/IconButton'
import ImageIcon from '@mui/icons-material/ImageOutlined'
import LockIcon from '@mui/icons-material/LockOutlined'
import DeleteForeverIcon from '@mui/icons-material/DeleteForeverOutlined'
import Divider from '@mui/material/Divider'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreIcon from '@mui/icons-material/MoreVertRounded'
import MoveIcon from '@mui/icons-material/DriveFileMoveOutlined'
import NoMatchIcon from '@mui/icons-material/SearchOffRounded'
import OpenIcon from '@mui/icons-material/VisibilityOutlined'
import PlayIcon from '@mui/icons-material/PlayArrowRounded'
import PdfIcon from '@mui/icons-material/PictureAsPdfOutlined'
import RecentIcon from '@mui/icons-material/ScheduleRounded'
import RenameIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import RestoreIcon from '@mui/icons-material/RestoreFromTrashOutlined'
import StarIcon from '@mui/icons-material/StarOutlineRounded'
import StarredIcon from '@mui/icons-material/StarRounded'
import TrashIcon from '@mui/icons-material/DeleteOutlineRounded'
import UnlockIcon from '@mui/icons-material/LockOpenOutlined'
import VideoIcon from '@mui/icons-material/MovieOutlined'

import {Text} from '../shared/blocks/Texts'

import {fileExtension, fileKind, formatBytes, formatTimestamp, isFailed, isSecured, isSecuring, previewKind} from '../../lib/fileTools'
import {formatClock, hasWaveform, waveHeights} from './MediaPlayer'
import useItemDrop, {ITEMS_TYPE} from '../../lib/hooks/useItemDrop'
import useSeen from '../../lib/hooks/useSeen'

import './driveItems.scss'

export const KIND_ICONS = {
  image: ImageIcon,
  video: VideoIcon,
  audio: AudioIcon,
  pdf: PdfIcon,
  doc: DocIcon,
  code: CodeIcon,
  file: FileIcon
}

export const selectionKey = ({kind, id}) => `${kind}:${id}`

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`

const removals = (entry, {inTrash, onTrash, onRestore, onPurge}) => (inTrash ?
  {restore: () => onRestore(entry), purge: () => onPurge(entry)} :
  {trash: () => onTrash(entry)})

const selecting = (entry, {selected, onToggleSelect}) => ({
  selected: selected.has(selectionKey(entry)),
  toggleSelected: () => onToggleSelect(entry)
})

const moving = (entry, {inTrash, inLocked, onMove, onSeal, onDragItems, onDragEnd}) => (inTrash ?
  {} :
  {
    move: () => onMove(entry),
    seal: onSeal ? {out: Boolean(inLocked), run: () => onSeal(entry)} : null,
    drag: onDragItems ? {start: () => onDragItems(entry), end: onDragEnd} : null
  })

const renaming = (entry, {inTrash, onRename}) => (inTrash ? {} : {rename: () => onRename(entry)})

const entryActions = (entry, actions) => ({
  ...removals(entry, actions),
  ...renaming(entry, actions),
  ...moving(entry, actions),
  ...selecting(entry, actions)
})

const starring = (file, entry, {inTrash, inLocked, onStar, onUnstar}) => {
  if (inTrash || inLocked) return {}

  return file.starredAt ? {unstar: () => onUnstar(entry)} : {star: () => onStar(entry)}
}

const folderItem = (folder, itemCount, bytes, actions) => ({
  id: folder.id,
  name: folder.name,
  Icon: FolderIcon,
  to: actions.inTrash ? null : `${actions.folderPath}/${folder.id}`,
  ...entryActions({kind: 'folder', id: folder.id, name: folder.name}, actions),
  dropInto: actions.dropInto?.(folder) ?? null,
  size: actions.inTrash || !bytes ? '—' : formatBytes(bytes),
  modified: formatTimestamp(folder.updatedAt),
  // What is in a folder in Trash isn't listed, so there is nothing to count.
  summary: actions.inTrash ? formatTimestamp(folder.updatedAt) : plural(itemCount, 'item')
})

// How the tile of each kind the worker pictures wears its picture. An image wears it plainly.
const THUMBNAIL_LOOKS = {image: null, video: 'media', pdf: 'page', audio: 'wave'}

// The kinds the worker sets out the opening lines of, and how each is set.
const EXCERPT_LOOKS = {text: 'page', code: 'code'}

const thumbnailLook = (file, kind) => {
  if (file.thumbnail?.excerpt) return EXCERPT_LOOKS[previewKind(file.filename)] ?? 'page'

  return THUMBNAIL_LOOKS[kind] ?? null
}

// Whether a thumbnail is on its way for a file still being secured, judged from its name.
const awaitsThumbnail = (file, kind) => kind in THUMBNAIL_LOOKS || previewKind(file.filename) in EXCERPT_LOOKS

const DEFAULT_RATIO = 4 / 3
const NARROWEST_RATIO = 3 / 4
const WIDEST_RATIO = 2

const thumbnailRatio = thumbnail => {
  if (!thumbnail?.url || !thumbnail.width || !thumbnail.height) return DEFAULT_RATIO

  return Math.min(WIDEST_RATIO, Math.max(NARROWEST_RATIO, thumbnail.width / thumbnail.height))
}

const thumbnailBadge = (kind, thumbnail) => {
  if (['video', 'audio'].includes(kind) && Number.isFinite(thumbnail?.durationMs)) return formatClock(thumbnail.durationMs / 1000)
  if (kind === 'pdf' && Number.isFinite(thumbnail?.pageCount)) return plural(thumbnail.pageCount, 'page')

  return null
}

const cardSummary = (file, failed, modified) => {
  if (failed) return `Failed · ${modified}`

  return file.filesize === null ? modified : `${formatBytes(file.filesize)} · ${modified}`
}

// How far from the end of the files listed the next page is asked for.
const NEAR_END = 10

const fileItem = (file, actions, nearEnd = false) => {
  const modified = formatTimestamp(file.updatedAt)
  const secured = isSecured(file)
  const failed = isFailed(file) && !actions.inTrash
  const opens = secured && !actions.inTrash
  const entry = {kind: 'file', id: file.id, name: file.filename}
  const kind = fileKind(file.filename)

  return {
    id: file.id,
    name: file.filename,
    Icon: KIND_ICONS[kind],
    thumbnail: {
      url: file.thumbnail?.url ?? null,
      excerpt: file.thumbnail?.excerpt ?? null,
      wave: hasWaveform(file) ? waveHeights(file) : null,
      markdown: fileExtension(file.filename) === 'md',
      pending: ((awaitsThumbnail(file, kind) && isSecuring(file)) || Boolean(actions.picturePending?.(file))) && !actions.inTrash,
      look: thumbnailLook(file, kind),
      badge: thumbnailBadge(kind, file.thumbnail),
      ratio: thumbnailRatio(file.thumbnail),
      renew: actions.onThumbnailFailed ? () => actions.onThumbnailFailed(file) : null
    },
    open: opens ? () => actions.onOpenFile(file) : null,
    download: opens ? () => actions.onDownloadFile(file) : null,
    securing: !secured && !failed && !actions.inTrash,
    failed,
    ...entryActions(entry, actions),
    ...starring(file, entry, actions),
    size: failed ? 'Failed' : formatBytes(file.filesize),
    modified,
    // Size is only known once the file has been secured, and a card has no column to leave blank.
    summary: cardSummary(file, failed, modified),
    seen: nearEnd ? actions.onNearEnd ?? null : null
  }
}

const fileItems = (files, actions) => files.map((file, index) => fileItem(file, actions, index === Math.max(0, files.length - NEAR_END)))

const itemShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  // Files only.
  thumbnail: PropTypes.shape({
    url: PropTypes.string,
    pending: PropTypes.bool.isRequired,
    excerpt: PropTypes.string,
    wave: PropTypes.arrayOf(PropTypes.number),
    markdown: PropTypes.bool.isRequired,
    look: PropTypes.oneOf(['media', 'page', 'code', 'wave']),
    badge: PropTypes.string,
    // Width over height, for the card to take before its picture arrives.
    ratio: PropTypes.number.isRequired,
    // Asks for a fresh url, for a picture that failed to load.
    renew: PropTypes.func
  }),
  to: PropTypes.string,
  open: PropTypes.func,
  download: PropTypes.func,
  securing: PropTypes.bool,
  // Files only: never secured, and only good for deleting.
  failed: PropTypes.bool,
  rename: PropTypes.func,
  move: PropTypes.func,
  // Into Locked from the drive, or "out" of it from Locked.
  seal: PropTypes.shape({out: PropTypes.bool.isRequired, run: PropTypes.func.isRequired}),
  // In the drive. "start" is told the drag has begun and answers how many items it carries.
  drag: PropTypes.shape({start: PropTypes.func.isRequired, end: PropTypes.func.isRequired}),
  // Folders only, while something they can take is being dragged.
  dropInto: PropTypes.func,
  star: PropTypes.func,
  unstar: PropTypes.func,
  trash: PropTypes.func,
  restore: PropTypes.func,
  purge: PropTypes.func,
  selected: PropTypes.bool.isRequired,
  toggleSelected: PropTypes.func.isRequired,
  size: PropTypes.string.isRequired,
  modified: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  // Files only, the one the next page is asked for at: called as it scrolls into view.
  seen: PropTypes.func
})

const frameTitle = item => {
  if (item.failed) return 'This file couldn’t be secured. Delete it, then try again.'

  return item.securing ? 'Still being secured' : null
}

// A folder is a link to its files and a secured file a button that opens it; a file still being
// secured is neither, since there is nothing to open yet, and nor is anything in Trash.
const ItemFrame = ({item, className, children}) => {
  if (item.to) return <Link className={classnames(className, 'opens folder')} to={item.to} draggable={false}>{children}</Link>
  if (item.open) return <button type='button' className={classnames(className, 'opens')} onClick={item.open}>{children}</button>

  return <div className={className} title={frameTitle(item)}>{children}</div>
}

ItemFrame.propTypes = {
  item: itemShape.isRequired,
  className: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

const RowMenu = ({item}) => {
  const [menuAnchor, setMenuAnchor] = useState(null)

  const choose = action => () => {
    setMenuAnchor(null)
    action()
  }

  return (
    <>
      <IconButton
        className='row-menu-button'
        aria-label={`Actions for ${item.name}`}
        aria-haspopup='menu'
        aria-expanded={Boolean(menuAnchor)}
        onClick={event => setMenuAnchor(event.currentTarget)}>
        <MoreIcon fontSize='small' />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        slots={{transition: Fade}}
        transitionDuration={0}
        slotProps={{paper: {className: 'drive-menu'}}}>
        {item.open && <MenuItem onClick={choose(item.open)}><OpenIcon />Open</MenuItem>}
        {item.download && <MenuItem onClick={choose(item.download)}><DownloadIcon />Download</MenuItem>}
        {item.star && <MenuItem onClick={choose(item.star)}><StarIcon />Add to Starred</MenuItem>}
        {item.unstar && <MenuItem onClick={choose(item.unstar)}><StarredIcon />Remove from Starred</MenuItem>}
        {item.restore && <MenuItem onClick={choose(item.restore)}><RestoreIcon />Restore</MenuItem>}
        {(item.open || item.star || item.unstar || item.restore) && <Divider />}
        {item.rename && <MenuItem onClick={choose(item.rename)}><RenameIcon />Rename</MenuItem>}
        {item.move && <MenuItem onClick={choose(item.move)}><MoveIcon />Move to…</MenuItem>}
        {item.seal && (
          <MenuItem className='menu-locked' onClick={choose(item.seal.run)}>
            {item.seal.out ? <UnlockIcon /> : <LockIcon />}{item.seal.out ? 'Move out of Locked' : 'Move to Locked'}
          </MenuItem>
        )}
        {item.trash && <MenuItem className='menu-danger' onClick={choose(item.trash)}><TrashIcon />Delete</MenuItem>}
        {item.purge && <MenuItem className='menu-danger' onClick={choose(item.purge)}><DeleteForeverIcon />Delete forever</MenuItem>}
      </Menu>
    </>
  )
}

RowMenu.propTypes = {item: itemShape.isRequired}

const SelectCheck = ({item}) => (
  <Checkbox
    className='item-check'
    checked={item.selected}
    onChange={item.toggleSelected}
    disableRipple
    icon={<span className='check-box' />}
    checkedIcon={<span className='check-box'><CheckIcon /></span>}
    slotProps={{input: {'aria-label': `Select ${item.name}`}}}
  />
)

SelectCheck.propTypes = {item: itemShape.isRequired}

const MARKDOWN_HEADING = /^#{1,6}\s/

// One line to a span, so a heading can be set apart from the lines around it.
const ExcerptSheet = ({excerpt, markdown}) => (
  <span className='thumb-sheet' aria-hidden='true'>
    {excerpt.split('\n').map((line, index) => (
      <span key={index} className={classnames('sheet-line', {heading: markdown && MARKDOWN_HEADING.test(line)})}>{line}</span>
    ))}
  </span>
)

ExcerptSheet.propTypes = {
  excerpt: PropTypes.string.isRequired,
  markdown: PropTypes.bool.isRequired
}

// A tile has less room than the player for its bars, so neighbours are drawn as one, as tall as the
// louder of them, rather than any peak being dropped.
const BARS_PER_TILE = {card: 42, row: 14}

const gatheredBars = (heights, count) => Array.from({length: count}, (bar, index) => {
  const start = Math.floor(index * heights.length / count)
  const end = Math.max(start + 1, Math.floor((index + 1) * heights.length / count))

  return Math.max(...heights.slice(start, end))
})

const WaveBars = ({heights, size}) => (
  <span className='thumb-wave' aria-hidden='true'>
    {gatheredBars(heights, BARS_PER_TILE[size]).map((height, index) => <i key={index} style={{height: `${height}%`}} />)}
  </span>
)

WaveBars.propTypes = {
  heights: PropTypes.arrayOf(PropTypes.number).isRequired,
  size: PropTypes.oneOf(['card', 'row']).isRequired
}

const ThumbnailFace = ({item, size, picture, onFailed}) => {
  const {url, excerpt, wave, markdown} = item.thumbnail

  if (picture) return <img className='thumb-art' src={url} alt='' loading='lazy' decoding='async' draggable={false} onError={onFailed} />
  if (excerpt) return <ExcerptSheet excerpt={excerpt} markdown={markdown} />
  if (wave) return <WaveBars heights={wave} size={size} />

  return <item.Icon className='item-glyph' />
}

ThumbnailFace.propTypes = {
  item: itemShape.isRequired,
  size: PropTypes.oneOf(['card', 'row']).isRequired,
  picture: PropTypes.bool.isRequired,
  onFailed: PropTypes.func.isRequired
}

// Keyed by its url where it is used, so a fresh url gets another try at a picture that failed.
const Thumbnail = ({item, className, size}) => {
  const [failed, setFailed] = useState(false)
  const {url, excerpt, wave, pending, look, badge, renew} = item.thumbnail
  const picture = Boolean(url) && !failed
  const words = !picture && Boolean(excerpt)
  const sound = !picture && !words && Boolean(wave)
  const dark = (picture && look !== 'page') || sound

  const giveUp = () => {
    setFailed(true)
    renew?.()
  }

  return (
    <span className={classnames(className, 'thumb', look, {picture, words, sound, dark, pending: !picture && !words && !sound && pending})}>
      <ThumbnailFace item={item} size={size} picture={picture} onFailed={giveUp} />
      {picture && look === 'media' && <span className='thumb-play'><PlayIcon /></span>}
      {size === 'card' && (
        <span className='thumb-chip'>
          <item.Icon className='chip-kind' />
          {badge && <span className='thumb-badge'>{badge}</span>}
        </span>
      )}
    </span>
  )
}

Thumbnail.propTypes = {
  item: itemShape.isRequired,
  className: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['card', 'row']).isRequired
}

const RowLead = ({item}) => {
  if (!item.thumbnail) return <span className='row-glyph'><item.Icon className='item-glyph' /></span>

  return <Thumbnail key={item.thumbnail.url} item={item} className='row-thumb' size='row' />
}

RowLead.propTypes = {item: itemShape.isRequired}

// Shown under the pointer for a drag of several items, then taken away: the browser keeps a picture
// of it for as long as the drag lasts.
const showCount = (dataTransfer, count) => {
  const label = document.createElement('div')

  label.className = 'drag-count'
  label.textContent = plural(count, 'item')
  document.body.appendChild(label)
  dataTransfer.setDragImage?.(label, 18, 18)
  setTimeout(() => label.remove())
}

// What makes a row or card something to drag, and a folder's a place to drop what is dragged.
const useDragAndDrop = item => {
  const [over, dropHandlers] = useItemDrop(item.dropInto)

  if (!item.drag) return {over, handlers: dropHandlers}

  const handleDragStart = event => {
    const count = item.drag.start()

    event.dataTransfer.setData(ITEMS_TYPE, String(count))
    event.dataTransfer.effectAllowed = 'move'
    if (count > 1) showCount(event.dataTransfer, count)
  }

  return {over, handlers: {draggable: true, onDragStart: handleDragStart, onDragEnd: item.drag.end, ...dropHandlers}}
}

const ItemRow = ({item}) => {
  const {over, handlers} = useDragAndDrop(item)
  const watch = useSeen(item.seen)

  return (
    <div ref={watch} className={classnames('item-row', {selected: item.selected, failed: item.failed, 'drop-target': over})} {...handlers}>
      <SelectCheck item={item} />
      <ItemFrame item={item} className='row-main'>
        <RowLead item={item} />
        <span className='row-name'>{item.name}</span>
      </ItemFrame>
      <span className='row-cell size'>{item.size}</span>
      <span className='row-cell modified'>{item.modified}</span>
      <span className='row-actions'><RowMenu item={item} /></span>
    </div>
  )
}

ItemRow.propTypes = {item: itemShape.isRequired}

const FolderCard = ({item}) => {
  const {over, handlers} = useDragAndDrop(item)

  return (
    <div className={classnames('item-card', {selected: item.selected, 'drop-target': over})} {...handlers}>
      <ItemFrame item={item} className='card-main'>
        <item.Icon className='item-glyph' />
        <span>
          <span className='card-name'>{item.name}</span>
          <span className='card-summary'>{item.summary}</span>
        </span>
      </ItemFrame>
      <SelectCheck item={item} />
    </div>
  )
}

FolderCard.propTypes = {item: itemShape.isRequired}

// The glass catches a highlight where the pointer is, as a lit surface would.
const followPointer = event => {
  const {left, top, width, height} = event.currentTarget.getBoundingClientRect()

  if (!width || !height) return

  event.currentTarget.style.setProperty('--glint-x', `${((event.clientX - left) / width) * 100}%`)
  event.currentTarget.style.setProperty('--glint-y', `${((event.clientY - top) / height) * 100}%`)
}

// The name is cut to one line on its caption, so the whole of it is given on hover.
const FileCard = ({item}) => {
  const {handlers} = useDragAndDrop(item)
  const watch = useSeen(item.seen)

  return (
    <div
      ref={watch}
      className={classnames('item-card', 'file-card', {selected: item.selected, failed: item.failed})}
      style={{'--ratio': item.thumbnail.ratio}}
      onPointerMove={followPointer}
      {...handlers}>
      <ItemFrame item={item} className='card-main'>
        <Thumbnail key={item.thumbnail.url} item={item} className='card-media' size='card' />
        <span className='card-caption'>
          <item.Icon className='card-kind' />
          <span className='card-text'>
            <span className='card-name' title={item.name}>{item.name}</span>
            <span className='card-summary'>{item.summary}</span>
          </span>
        </span>
      </ItemFrame>
      <SelectCheck item={item} />
    </div>
  )
}

FileCard.propTypes = {item: itemShape.isRequired}

const ItemCard = ({item}) => (item.thumbnail ? <FileCard item={item} /> : <FolderCard item={item} />)

ItemCard.propTypes = {item: itemShape.isRequired}

const ItemGroup = ({label, items, view, someSelected}) => (
  <section className='item-group'>
    <h2 className='group-label'>{label}</h2>
    {view === 'grid' ?
      <div className={classnames('item-grid', {selecting: someSelected, justified: items.some(item => item.thumbnail)})}>
        {items.map(item => <ItemCard key={item.id} item={item} />)}
      </div> :
      <div className='item-list'>
        <div className='list-head' aria-hidden='true'>
          <span />
          <span>Name</span>
          <span className='size'>Size</span>
          <span className='modified'>Modified</span>
          <span />
        </div>
        {items.map(item => <ItemRow key={item.id} item={item} />)}
      </div>}
  </section>
)

ItemGroup.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(itemShape).isRequired,
  view: PropTypes.oneOf(['list', 'grid']).isRequired,
  // Anything is selected, anywhere listed.
  someSelected: PropTypes.bool.isRequired
}

const EMPTY_COPY = {
  drive: [EmptyIcon, 'This folder is empty', 'Drop files here, or use New to add files, folders, or a folder from your machine.'],
  recent: [RecentIcon, 'Nothing opened yet', 'Files you preview or download show up here, the latest first.'],
  starred: [StarredIcon, 'Nothing starred yet', 'Star a file from its menu to find it here again.'],
  trash: [TrashIcon, 'Trash is empty', 'Deleted items rest here for 30 days before they are removed for good.'],
  locked: [LockIcon, 'Locked is empty', 'Items you move here are sealed on arrival. Drop files here, or use New, to add them.']
}

// Recent and Starred are lists of files from all over the drive, so they have no folders to group.
const FILES_ONLY = {recent: 'Recently opened', starred: 'Starred'}

const emptyCopy = (query, section) => {
  if (query) return [NoMatchIcon, `Nothing matches “${query}”`, 'Search covers names in this drive.']

  return EMPTY_COPY[section]
}

const EmptyState = ({query, section}) => {
  const [Icon, title, body] = emptyCopy(query, section)

  return (
    <div className='drive-empty'>
      <Icon className='empty-glyph' />
      <Text className='empty-title'>{title}</Text>
      <Text className='empty-body'>{body}</Text>
    </div>
  )
}

EmptyState.propTypes = {
  query: PropTypes.string.isRequired,
  section: PropTypes.oneOf(Object.keys(EMPTY_COPY)).isRequired
}

const DriveItems = ({folders, files, itemCount, folderSize, view, query, section, selected, folderPath = '/folders', ...handlers}) => {
  if (!folders.length && !files.length) return <EmptyState query={query} section={section} />

  const actions = {inTrash: section === 'trash', inLocked: section === 'locked', selected, folderPath, ...handlers}
  const layout = {view, someSelected: selected.size > 0}

  if (FILES_ONLY[section]) return <ItemGroup label={FILES_ONLY[section]} {...layout} items={fileItems(files, actions)} />

  return (
    <>
      {folders.length > 0 && (
        <ItemGroup label='Folders' {...layout} items={folders.map(folder => folderItem(folder, itemCount(folder.id), folderSize(folder.id), actions))} />
      )}
      {files.length > 0 && <ItemGroup label='Files' {...layout} items={fileItems(files, actions)} />}
    </>
  )
}

DriveItems.propTypes = {
  folders: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired
  })).isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    filesize: PropTypes.number,
    status: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    starredAt: PropTypes.string,
    thumbnail: PropTypes.shape({
      url: PropTypes.string,
      durationMs: PropTypes.number,
      pageCount: PropTypes.number,
      excerpt: PropTypes.string,
      peaks: PropTypes.arrayOf(PropTypes.number)
    })
  })).isRequired,
  // The folders and files directly in a folder, given its id.
  itemCount: PropTypes.func.isRequired,
  // The bytes a folder holds, below it too, given its id.
  folderSize: PropTypes.func.isRequired,
  view: PropTypes.oneOf(['list', 'grid']).isRequired,
  query: PropTypes.string.isRequired,
  section: PropTypes.oneOf(Object.keys(EMPTY_COPY)).isRequired,
  onOpenFile: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  // Each is handed the item as "{kind: 'folder' | 'file', id, name}".
  onTrash: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  onPurge: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onStar: PropTypes.func.isRequired,
  onUnstar: PropTypes.func.isRequired,
  // The "selectionKey" of each selected item.
  selected: PropTypes.instanceOf(Set).isRequired,
  // Handed the item the same way.
  onToggleSelect: PropTypes.func.isRequired,
  // Where items can cross between the drive and Locked: handed the item, as the others are.
  onSeal: PropTypes.func,
  // Where a folder's link leads, before its id.
  folderPath: PropTypes.string,
  // Where a thumbnail's url can run out: handed the file whose picture failed to load.
  onThumbnailFailed: PropTypes.func,
  // Where pictures are asked for after the listing, as in Locked: handed a file, answers whether
  // its picture is still on its way.
  picturePending: PropTypes.func,
  // Where items can be dragged: handed the item a drag starts on, and answers how many it carries.
  onDragItems: PropTypes.func,
  onDragEnd: PropTypes.func,
  // While something is dragged: handed a folder, answers what to do when it is dropped there, or
  // null when the folder can't take it.
  dropInto: PropTypes.func,
  // Where the files listed are only the pages so far and more follow: asks for the next page.
  onNearEnd: PropTypes.func
}

export default DriveItems
